import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import PublicNavbar from "@/components/layout/PublicNavbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Plus, ArrowLeft, Lock, Globe, User, Send,
  Loader2, Clock, MessageCircle, ChevronRight,
} from "lucide-react";

const authorBadge = (type) =>
  type === "student"
    ? <Badge className="bg-sky-100 text-sky-700 text-[10px] px-1.5 py-0">Student</Badge>
    : <Badge className="bg-slate-100 text-slate-500 text-[10px] px-1.5 py-0">Guest</Badge>;

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function CommunityPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postDetail, setPostDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newPostDialog, setNewPostDialog] = useState(false);
  const [postForm, setPostForm] = useState({ title: "", content: "", guest_name: "" });
  const [commentText, setCommentText] = useState("");
  const [commentGuestName, setCommentGuestName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/forum/categories").then((r) => {
      setCategories(r.data);
      if (r.data.length > 0) setSelectedCat(r.data[0]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedCat) return;
    setLoadingPosts(true);
    setSelectedPost(null);
    setPostDetail(null);
    api.get(`/forum/posts?category=${selectedCat.slug}`)
      .then((r) => { setPosts(r.data.posts); setPostsTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoadingPosts(false));
  }, [selectedCat]);

  const loadPostDetail = (postId) => {
    setLoadingDetail(true);
    setSelectedPost(postId);
    api.get(`/forum/posts/${postId}`)
      .then((r) => setPostDetail(r.data))
      .catch(() => toast.error("Failed to load post"))
      .finally(() => setLoadingDetail(false));
  };

  const handleNewPost = async (e) => {
    e.preventDefault();
    if (!postForm.title.trim() || !postForm.content.trim()) { toast.error("Title and content required"); return; }
    setSubmitting(true);
    try {
      const payload = { category_id: selectedCat._id, title: postForm.title, content: postForm.content };
      if (!user) payload.guest_name = postForm.guest_name || "Guest";
      await api.post("/forum/posts", payload);
      toast.success("Post created!");
      setNewPostDialog(false);
      setPostForm({ title: "", content: "", guest_name: "" });
      // Refresh posts
      const r = await api.get(`/forum/posts?category=${selectedCat.slug}`);
      setPosts(r.data.posts);
      setPostsTotal(r.data.total);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const payload = { content: commentText };
      if (!user) payload.guest_name = commentGuestName || "Guest";
      await api.post(`/forum/posts/${selectedPost}/comments`, payload);
      toast.success("Comment added!");
      setCommentText("");
      setCommentGuestName("");
      loadPostDetail(selectedPost);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const canPostInCategory = (cat) => !cat.restricted || !!user;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="community-page">
      <PublicNavbar />

      <div className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <span className="text-sky-500 font-heading font-bold text-xs uppercase tracking-[0.25em]">Community</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter mt-1">Student Forum</h1>
            <p className="text-slate-500 mt-2">Connect with fellow students and the Legacy Heights community.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Category Sidebar */}
            <div className="lg:col-span-1" data-testid="forum-categories">
              <div className="space-y-1.5">
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /></div>
                ) : (
                  categories.map((cat) => (
                    <button
                      key={cat._id}
                      onClick={() => setSelectedCat(cat)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm ${
                        selectedCat?._id === cat._id
                          ? "bg-sky-500 text-white shadow-lg shadow-sky-500/20"
                          : "bg-white border border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50"
                      }`}
                      data-testid={`category-btn-${cat.slug}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {cat.restricted ? <Lock className="h-3.5 w-3.5 shrink-0" /> : <Globe className="h-3.5 w-3.5 shrink-0" />}
                          <span className="font-medium">{cat.name}</span>
                        </div>
                        <span className={`text-xs ${selectedCat?._id === cat._id ? "text-white/70" : "text-slate-400"}`}>
                          {cat.post_count || 0}
                        </span>
                      </div>
                      {cat.restricted && (
                        <span className={`text-[10px] ml-5.5 ${selectedCat?._id === cat._id ? "text-white/60" : "text-amber-600"}`}>
                          Students only
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>

              {user && (
                <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-700">
                  <User className="h-3.5 w-3.5 inline mr-1" />
                  Signed in as <strong>{user.name}</strong> — your posts are labeled as Student.
                </div>
              )}
              {!user && (
                <div className="mt-4 p-3 bg-slate-100 border border-slate-200 rounded-lg text-xs text-slate-600">
                  <Globe className="h-3.5 w-3.5 inline mr-1" />
                  Posting as Guest. <a href="/login" className="text-sky-600 font-semibold underline">Sign in</a> to post as a Student and access restricted categories.
                </div>
              )}
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {!selectedPost ? (
                /* Post List View */
                <div>
                  {selectedCat && (
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="font-heading text-xl font-bold text-slate-900">{selectedCat.name}</h2>
                        <p className="text-sm text-slate-500 mt-0.5">{selectedCat.description}</p>
                      </div>
                      {canPostInCategory(selectedCat) && (
                        <Button
                          className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-5"
                          onClick={() => setNewPostDialog(true)}
                          data-testid="new-post-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" /> New Post
                        </Button>
                      )}
                      {!canPostInCategory(selectedCat) && (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Lock className="h-3 w-3 mr-1" /> Sign in to post
                        </Badge>
                      )}
                    </div>
                  )}

                  {loadingPosts ? (
                    <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
                  ) : posts.length === 0 ? (
                    <Card className="border-slate-200">
                      <CardContent className="text-center py-16">
                        <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No posts yet. Be the first to start a discussion!</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {posts.map((post) => (
                        <Card
                          key={post._id}
                          className="border-slate-200 cursor-pointer hover:border-sky-300 hover:shadow-sm transition-all"
                          onClick={() => loadPostDetail(post._id)}
                          data-testid={`post-card-${post._id}`}
                        >
                          <CardContent className="py-4 px-5">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-heading font-bold text-slate-900 truncate">{post.title}</h3>
                                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{post.content}</p>
                                <div className="flex items-center gap-3 mt-2.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-slate-600 font-medium">{post.author_name}</span>
                                    {authorBadge(post.author_type)}
                                  </div>
                                  <span className="text-xs text-slate-400 flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> {timeAgo(post.created_at)}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 text-slate-400 shrink-0">
                                <MessageCircle className="h-4 w-4" />
                                <span className="text-xs font-medium">{post.comment_count || 0}</span>
                                <ChevronRight className="h-4 w-4 ml-1" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Post Detail View */
                <div>
                  <button
                    onClick={() => { setSelectedPost(null); setPostDetail(null); }}
                    className="flex items-center gap-1 text-sm text-slate-500 hover:text-sky-600 transition-colors mb-4"
                    data-testid="back-to-posts"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back to posts
                  </button>

                  {loadingDetail ? (
                    <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
                  ) : postDetail ? (
                    <div className="space-y-6">
                      {/* Post */}
                      <Card className="border-slate-200" data-testid="post-detail">
                        <CardContent className="py-6 px-6">
                          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-3">{postDetail.title}</h2>
                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-sm font-medium text-slate-700">{postDetail.author_name}</span>
                            {authorBadge(postDetail.author_type)}
                            <span className="text-xs text-slate-400">{timeAgo(postDetail.created_at)}</span>
                            <Badge variant="outline" className="text-[10px]">{postDetail.category_name}</Badge>
                          </div>
                          <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{postDetail.content}</p>
                        </CardContent>
                      </Card>

                      {/* Comments */}
                      <div>
                        <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                          <MessageCircle className="h-5 w-5 text-sky-500" />
                          {postDetail.comments?.length || 0} Comments
                        </h3>

                        {(postDetail.comments || []).length > 0 ? (
                          <div className="space-y-3">
                            {postDetail.comments.map((c) => (
                              <div key={c._id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`comment-${c._id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-medium text-slate-700">{c.author_name}</span>
                                  {authorBadge(c.author_type)}
                                  <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed">{c.content}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 py-4">No comments yet. Be the first to reply!</p>
                        )}

                        {/* Add Comment */}
                        <Separator className="my-6" />
                        <form onSubmit={handleComment} className="space-y-3" data-testid="comment-form">
                          {!user && (
                            <div>
                              <Label className="text-slate-700 text-sm">Your Name</Label>
                              <Input
                                value={commentGuestName}
                                onChange={(e) => setCommentGuestName(e.target.value)}
                                placeholder="Guest"
                                className="h-10 mt-1 border-slate-200"
                                data-testid="comment-guest-name"
                              />
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Textarea
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              placeholder="Write a comment..."
                              className="flex-1 min-h-[80px] border-slate-200"
                              data-testid="comment-input"
                            />
                          </div>
                          <Button
                            type="submit"
                            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6"
                            disabled={submitting || !commentText.trim()}
                            data-testid="submit-comment-btn"
                          >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Post Comment</>}
                          </Button>
                        </form>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* New Post Dialog */}
      <Dialog open={newPostDialog} onOpenChange={setNewPostDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="new-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Post in {selectedCat?.name}</DialogTitle>
            {selectedCat?.restricted && (
              <p className="text-xs text-amber-600 flex items-center gap-1 mt-1"><Lock className="h-3 w-3" /> Students only category</p>
            )}
          </DialogHeader>
          <form onSubmit={handleNewPost} className="space-y-4">
            {!user && (
              <div>
                <Label className="text-slate-700">Your Name</Label>
                <Input
                  value={postForm.guest_name}
                  onChange={(e) => setPostForm((p) => ({ ...p, guest_name: e.target.value }))}
                  placeholder="Guest"
                  className="h-10 mt-1.5 border-slate-200"
                  data-testid="post-guest-name"
                />
              </div>
            )}
            <div>
              <Label className="text-slate-700">Title</Label>
              <Input
                value={postForm.title}
                onChange={(e) => setPostForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="What's on your mind?"
                className="h-10 mt-1.5 border-slate-200"
                required
                data-testid="post-title-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">Content</Label>
              <Textarea
                value={postForm.content}
                onChange={(e) => setPostForm((p) => ({ ...p, content: e.target.value }))}
                placeholder="Share your thoughts..."
                className="mt-1.5 min-h-[120px] border-slate-200"
                required
                data-testid="post-content-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewPostDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6" disabled={submitting} data-testid="submit-new-post-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Post"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
